import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Save, Plus, Trash2, Upload, MoveUp, MoveDown } from 'lucide-react';

export const GalleryManager = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ items: [] });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gallery`);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/gallery`, formData, {
        headers: getAuthHeader(),
      });
      alert('Gallery updated successfully!');
    } catch (error) {
      console.error('Error updating gallery:', error);
      alert('Failed to update gallery');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (file) => {
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload`, formDataUpload, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data',
        },
      });

      const fileType = file.type.startsWith('video/') ? 'video' : 'image';
      const newItem = {
        id: `item-${Date.now()}`,
        type: fileType,
        url: response.data.url,
        filename: response.data.filename,
        order: formData.items.length,
      };

      setFormData({ ...formData, items: [...formData.items, newItem] });
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file');
    }
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const moveItem = (index, direction) => {
    const newItems = [...formData.items];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newItems.length) return;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    newItems.forEach((item, i) => (item.order = i));
    setFormData({ ...formData, items: newItems });
  };

  if (loading) {
    return <div className="text-white">Loading...</div>;
  }

  return (
    <div className="max-w-4xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-white">Gallery Items</h3>
            <label className="cursor-pointer">
              <div className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                <Upload className="w-5 h-5 mr-2" />
                Upload File
              </div>
              <input
                type="file"
                accept="image/*,video/*"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleFileUpload(e.target.files[0]);
                  }
                }}
                className="hidden"
              />
            </label>
          </div>

          <div className="space-y-3">
            {formData.items.map((item, index) => (
              <div
                key={index}
                className="bg-gray-700 border border-gray-600 rounded-lg p-4 flex items-center gap-4"
              >
                <div className="flex-shrink-0">
                  {item.type === 'image' ? (
                    <img
                      src={item.url}
                      alt="Gallery item"
                      className="w-24 h-24 object-cover rounded"
                    />
                  ) : (
                    <video src={item.url} className="w-24 h-24 object-cover rounded" />
                  )}
                </div>

                <div className="flex-1">
                  <p className="text-white font-medium">
                    {item.type === 'image' ? 'Image' : 'Video'}
                  </p>
                  <p className="text-gray-400 text-sm truncate">{item.filename || item.url}</p>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => moveItem(index, 'up')}
                    disabled={index === 0}
                    className="p-2 bg-gray-600 text-white rounded hover:bg-gray-500 disabled:opacity-30"
                  >
                    <MoveUp className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveItem(index, 'down')}
                    disabled={index === formData.items.length - 1}
                    className="p-2 bg-gray-600 text-white rounded hover:bg-gray-500 disabled:opacity-30"
                  >
                    <MoveDown className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="p-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}

            {formData.items.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <p>No gallery items yet. Upload some images or videos!</p>
              </div>
            )}
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="flex items-center px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
        >
          <Save className="w-5 h-5 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
};